import { createInertiaApp } from "@inertiajs/react";
import { createElement, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { SiteLayout } from "../frontend/layouts/site-layout";
import { initPostHog } from "../frontend/lib/analytics";

// Import global styles here for Vite to process
import "@styles/index.scss";
import "react-toastify/dist/ReactToastify.css";

// Initialize Sentry error tracking. We pull config from Rails-rendered meta
// tags so user identity, release, and environment are known BEFORE any
// React code runs — that closes the race where errors during initial render
// land in Sentry without user/release context.
const meta = (name: string): string | undefined =>
  document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content || undefined;

const sentryDsn = meta("sentry-dsn");
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    release: meta("sentry-release"),
    environment: meta("sentry-environment") || import.meta.env.MODE,
    sendDefaultPii: false,
    // Default is 100; bigger buffer means more of the user's session
    // (console logs, fetches, clicks) gets attached to each event.
    maxBreadcrumbs: 200,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Promote console.error / console.warn calls to Sentry events so we
      // see "something logged a problem" without needing a thrown exception.
      Sentry.captureConsoleIntegration({ levels: ["error", "warn"] }),
      // Session replay: 10% of sessions sampled; 100% of sessions that hit
      // an error get replayed. Masks text/inputs by default for privacy.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  // Identify the user immediately, before React renders. The layout-level
  // useSentryIdentify still runs to refresh tags as Inertia navigates and
  // project state changes, but this catches errors that fire during initial
  // render.
  const userJson = meta("sentry-user");
  if (userJson) {
    try {
      const u = JSON.parse(userJson);
      Sentry.setUser({ id: String(u.id), email: u.email });
      Sentry.setTag("role", u.admin ? "admin" : "user");
      Sentry.setTag("impersonating", String(!!u.impersonating));
    } catch {
      // Bad JSON shouldn't kill bootstrap.
    }
  }
}

// Initialize PostHog analytics
initPostHog();

// Temporary type definition, until @inertiajs/react provides one
type PageComponent = React.ComponentType & {
  layout?: (page: ReactNode) => ReactNode;
};

type ResolvedComponent = {
  default: PageComponent;
  layout?: (page: ReactNode) => ReactNode;
};

const appName = "Launch10";

createInertiaApp({
  title: (title) => (title ? `${title} - ${appName}` : appName),
  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../frontend/pages/**/*.tsx", {
      eager: true,
    });
    const page = pages[`../frontend/pages/${name}.tsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    if (page?.default) {
      page.default.layout ||= (p: ReactNode) => createElement(SiteLayout, null, p);
    }

    return page;
  },
  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(createElement(App, props));
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.'
      );
    }
  },
  progress: {
    color: "#4B5563",
    showSpinner: true,
  },
});
